'use client';

import { Calendar, Circle, Clock, FileText, Mail, MessageSquare, Send, Trophy, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { RelationshipStage, RelationshipStatus } from '@/types/report-types';

type StatusIcon = LucideIcon;

export const relationshipStatusConfig: Record<RelationshipStatus, {
  label: string;
  icon: StatusIcon;
  color: string;
  bgColor: string;
}> = {
  not_contacted: {
    label: 'Not Contacted',
    icon: Circle,
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
  },
  to_contact: {
    label: 'To Contact',
    icon: Circle,
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
  },
  email_sent: {
    label: 'Email Sent',
    icon: Send,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50',
  },
  opened: {
    label: 'Opened',
    icon: Mail,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  replied: {
    label: 'Replied',
    icon: MessageSquare,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-50',
  },
  awaiting_reply: {
    label: 'Awaiting Reply',
    icon: Clock,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
  },
  in_conversation: {
    label: 'In Conversation',
    icon: MessageSquare,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50',
  },
  interview_scheduled: {
    label: 'Interview Scheduled',
    icon: Calendar,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
  },
  interviewing: {
    label: 'Interviewing',
    icon: Calendar,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  offer_received: {
    label: 'Offer Received',
    icon: Trophy,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-50',
  },
  no_response: {
    label: 'No Response',
    icon: Clock,
    color: 'text-orange-500',
    bgColor: 'bg-orange-50',
  },
  rejected: {
    label: 'Rejected',
    icon: X,
    color: 'text-red-500',
    bgColor: 'bg-red-50',
  },
  archived: {
    label: 'Archived',
    icon: FileText,
    color: 'text-gray-400',
    bgColor: 'bg-gray-50',
  },
};

export const activeConnectionStatuses: RelationshipStatus[] = [
  'to_contact',
  'email_sent',
  'awaiting_reply',
  'in_conversation',
  'interview_scheduled',
  'offer_received',
];

export const contactStatusOptions: RelationshipStatus[] = [
  'not_contacted',
  'to_contact',
  'email_sent',
  'opened',
  'replied',
  'awaiting_reply',
  'in_conversation',
  'interview_scheduled',
  'interviewing',
  'offer_received',
  'no_response',
  'rejected',
  'archived',
];

const stageMap: Record<RelationshipStatus, RelationshipStage> = {
  to_contact: 'outreach',
  not_contacted: 'outreach',
  email_sent: 'outreach',
  opened: 'opened',
  replied: 'replied',
  awaiting_reply: 'opened',
  in_conversation: 'replied',
  interview_scheduled: 'scheduled',
  interviewing: 'technical',
  offer_received: 'completed',
  no_response: 'no_response',
  rejected: 'closed',
  archived: 'closed',
};

export function getStageForStatus(status: RelationshipStatus): RelationshipStage {
  return stageMap[status] ?? 'outreach';
}
